import { call } from "redux-saga/effects";
import action from "../../decorators/action.decorator";
import reducer, { InitSchemaReducer } from "../../decorators/reducer.decorator";
import { DIContainerKeys } from "../../di-container/di-container.keys";
import { Entities } from "../../store/entities/entities.enum";
import { HttpService } from "../http.service";
import { ReducerMethods } from "../../store/reducer.methods";
import { API_DOMENS, BASE_API_PATHS } from "../../../constants/apis.constants";
import loader from "../../decorators/loader.decorator";
import { Loaders } from "../../store/loaders/loaders.enum";
import type GetCategoriesDto from "./dto/get-categories.dto";
import { CallToast } from "../../../Components/PriceCalculator/modals/CustomToast";
import type UpdateCategoryDto from "./dto/update-category.dto";
import type DeleteCategoryDto from "./dto/delete-category.dto";
import flagger from "../../decorators/flagger.decorator";
import { Flaggers } from "../../store/flaggers/flaggers.enum";

export const CategoryEffectActions = {
  getAllCategories: `${Entities.Category}_getAllCategories`,
  updateCategory: `${Entities.Category}_updateCategory`,
  deleteCategory: `${Entities.Category}_deleteCategory`,
};

const initSchema: InitSchemaReducer = {
  definitions: {  },
  options: { idAttribute: '' },
};

@reducer({
  entityName: Entities.Category,
  initSchema,
  aliasName: DIContainerKeys.CategoryService,
})
export class CategoryService extends HttpService {
  @loader(Loaders.GetCategoriesLoader)
  @action(DIContainerKeys.CategoryService)
  public *getAllCategories(dto: GetCategoriesDto): any {
    yield this.setLoaderState(Loaders.GetCategoriesLoader, true);
    const response = yield call(
      this.get,
      {
        url: BASE_API_PATHS.getAllCategories,
        domain: API_DOMENS.PSA_API,
        params: {
          ...(dto.sortColumn !== undefined && dto.sortOrder !== undefined ? {
            CategoryFilters: JSON.stringify({
              SearchCriterias: [],
              searchString: "",
              sortColumn: dto.sortColumn,
              sortOrder: dto.sortOrder,
            })
          } : {})
        }
      },
      ReducerMethods.UPDATE
    );
    if (!response?.Success) {
      yield this.setLoaderState(Loaders.GetCategoriesLoader, false);
      return;
    }
    const payload = response?.Data?.reduce((acc: any, item: any) => {
      acc[item.Id] = item;
      return acc;
    }, {})
    yield this.setEntityStateById(Entities.Category, dto.clientId, payload);
    yield this.setLoaderState(Loaders.GetCategoriesLoader, false);
  }

  @action(DIContainerKeys.CategoryService)
  public *updateCategory(dto: UpdateCategoryDto): any {
    const body = dto.IdToClone ? {
      IdToClone: dto.IdToClone,
    } : dto.category;
    const response = yield call(
      this.post, 
      {
        url: BASE_API_PATHS.createProductCategory,
        domain: API_DOMENS.PSA_API,
        body: this.toFormData({
          ...body,
          optionEnum98: dto.optionEnum98,
        }),
      },
      ReducerMethods.UPDATE
    );
    if (!response?.Success) {
      CallToast("Something went wrong", false, false, "v2style");
      return;
    }
    if (dto.IdToClone) {
      CallToast(response?.Message, true, false, "v2style");
      yield this.getAllCategories({ clientId: dto.clientId });
      return;
    }
    const resCategoryId = response?.Data?.[0]?.Id;
    if (resCategoryId) {
      const state = yield this.di.reduxStore.state()?.[Entities.Category]?.[dto.clientId] ?? {};
      let payload = { ...state };
      if (dto.category.parentCategoryId) {
        const subcats = state?.[dto.category.parentCategoryId]?.SubCatList ?? [];
        const newSubcats = [...subcats];
        const subCatIndex = newSubcats.findIndex((subcat: any) => subcat.Id === resCategoryId);
        if (subCatIndex !== -1) {
          newSubcats[subCatIndex] = {
            ...dto.category,
            Id: resCategoryId,
          }
        } else {
          newSubcats.push({
            ...dto.category,
            Id: resCategoryId,
          })
        }
        payload = {
          ...state,
          [dto.category.parentCategoryId]: {
            ...state[dto.category.parentCategoryId],
            SubCatList: newSubcats,
          },
        }
      } else {
        payload = {
          ...state,
          [resCategoryId]: {
            ...dto.category,
            Id: resCategoryId,
          },
        }
      }
      yield this.setEntityStateById(Entities.Category, dto.clientId, payload);
    }
    if (dto.callback) {
      dto.callback();
    }
  }

  @action(DIContainerKeys.CategoryService)
  @flagger(Flaggers.DeleteCategoryFlagger)
  @flagger(Flaggers.DeleteCategoryPromptFlagger)
  @loader(Loaders.DeleteCategoryLoader)
  public *deleteCategory(dto: DeleteCategoryDto): any {
    yield this.setLoaderState(Loaders.DeleteCategoryLoader, true);
    yield this.setFlaggerState(Flaggers.DeleteCategoryFlagger, {
      isLoading: true,
      categoryId: dto.categoryId,
    });
    const response = yield call(
      this.post,
      {
        url: BASE_API_PATHS.deleteProductCategory,
        domain: API_DOMENS.PSA_API,
        body: this.toFormData({
          Id: dto.categoryId,
        }),
      },
      ReducerMethods.UPDATE
    )
    yield this.setLoaderState(Loaders.DeleteCategoryLoader, false);
    yield this.setFlaggerState(Flaggers.DeleteCategoryPromptFlagger, null);
    if (!response?.Success) {
      yield this.setFlaggerState(Flaggers.DeleteCategoryFlagger, null);
      CallToast("Something went wrong", false, false, "v2style");
      return;
    }
    const showModal = response?.Data?.ClientList?.length > 0 || response?.Data?.ProductList?.length > 0;
    const isDeleted = !response?.Data?.ClientList?.length && !response?.Data?.ProductList?.length;
    if (isDeleted) {
      const state = yield this.di.reduxStore.state()?.[Entities.Category]?.[dto.clientId] ?? {};
      let newState = { ...state };
      if (dto.parentCategoryId) {
        const subcats = state[dto.parentCategoryId]?.SubCatList ?? [];
        newState = {
          ...state,
          [dto.parentCategoryId]: {
            ...state[dto.parentCategoryId],
            SubCatList: subcats.filter((subcat: any) => subcat.Id !== dto.categoryId),
          },
        }
      } else {
        delete newState[dto.categoryId];
      }
      yield this.setEntityStateById(Entities.Category, dto.clientId, newState);
    }
    yield this.setFlaggerState(Flaggers.DeleteCategoryFlagger, {
      isLoading: false,
      categoryId: dto.categoryId,
      showModal,
      clientList: response?.Data?.ClientList,
      productList: response?.Data?.ProductList,
      CategoryList: response?.Data?.CategoryList,
      isDeleted,
    });
  }
}
